import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { normaliserTexte } from '../../../lib/normaliser';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  telephone: string;
  email: string;
  adresse: string;
}

interface RendezVous {
  id: string;
  date_rdv: string;
  heure_debut: string;
  motif: string;
  statut: string;
  medecin_id: string;
  diagnostique?: string;
  traitement?: string;
  medicaments?: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
  user_id: string;
}

interface SuiviPatient {
  id: string;
  objet: string;
  message: string;
  diagnostique?: string;
  traitement?: string;
  notes_medicales?: string;
  statut: string;
  date_creation: string;
  medecin_expediteur: {
    nom: string;
    prenom: string;
    specialite: string;
  };
  medecin_destinataire: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Prescription {
  id: string;
  medicaments: string;
  posologie?: string;
  duree_traitement?: string;
  instructions_particulieres?: string;
  photo_url?: string;
  date_creation: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

export default function RecherchePatients() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientHistory, setPatientHistory] = useState<RendezVous[]>([]);
  const [suivis, setSuivnis] = useState<SuiviPatient[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(false);
  const [medecinSpecialite, setMedecinSpecialite] = useState('');
  const [medecinId, setMedecinId] = useState('');
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  
  // Modals
  const [showPartageModal, setShowPartageModal] = useState(false);
  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [showSuivisModal, setShowSuivisModal] = useState(false);
  const [showPrescriptionsModal, setShowPrescriptionsModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState<any[]>([]);
  
  // Forms
  const [selectedMedecin, setSelectedMedecin] = useState('');
  const [selectedRendezVous, setSelectedRendezVous] = useState<RendezVous | null>(null);
  const [suiviForm, setSuiviForm] = useState({
    objet: '',
    message: '',
    diagnostique: '',
    traitement: '',
    notes_medicales: ''
  });
  const [prescriptionForm, setPrescriptionForm] = useState({
    medicaments: '',
    posologie: '',
    duree_traitement: '',
    instructions_particulieres: '',
    photo_url: ''
  });
  const [appointmentForm, setAppointmentForm] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    telephone: '',
    email: '',
    date_rdv: '',
    heure_debut: '',
    heure_fin: '',
  });
  const [selectedMatchedPatient, setSelectedMatchedPatient] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [suiteLoadingId, setSuiteLoadingId] = useState<string | null>(null);
  const [suiteActionMsg, setSuiteActionMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    getMedecinInfo();
    loadMedecins();
  }, []);

  const getMedecinInfo = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('medecins')
      .select('id, specialite')
      .eq('user_id', user.id)
      .single();

    if (data) {
      setMedecinSpecialite(data.specialite);
      setMedecinId(data.id);
    }
  };

  const loadMedecins = async () => {
    const { data } = await supabase
      .from('medecins')
      .select('id, nom, prenom, specialite, user_id')
      .neq('user_id', user?.id)
      .order('nom');

    if (data) {
      setMedecins(data);
    }
  };

  const searchPatients = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setPatients([]);
      return;
    }

    setLoading(true);
    const normalized = normaliserTexte(term);

    // On charge les patients de la clinique puis on filtre côté client
    // pour être insensible aux accents et à la casse ("akoue" -> "Akoué").
    const { data, error } = await supabase
      .from('patients')
      .select('id, nom, prenom, date_naissance, telephone, email, adresse')
      .order('nom');

    if (error) {
      console.error('Erreur recherche:', error);
      setPatients([]);
    } else {
      const results = (data || []).filter((p: any) => {
        const nom = normaliserTexte(p.nom || '');
        const prenom = normaliserTexte(p.prenom || '');
        const nomComplet = normaliserTexte(`${p.prenom || ''} ${p.nom || ''}`);
        const nomCompletInverse = normaliserTexte(`${p.nom || ''} ${p.prenom || ''}`);
        return nom.includes(normalized)
          || prenom.includes(normalized)
          || nomComplet.includes(normalized)
          || nomCompletInverse.includes(normalized);
      });
      setPatients(results);
    }
    setLoading(false);
  };

  const viewPatientHistory = async (patient: Patient) => {
    setSelectedPatient(patient);
    setLoading(true);

    const { data, error } = await supabase
      .from('rendez_vous')
      .select(`
        *,
        medecins (
          nom,
          prenom,
          specialite
        )
      `)
      .eq('patient_id', patient.id)
      .order('date_rdv', { ascending: false })
      .order('heure_debut', { ascending: false });

    if (error) {
      console.error('Erreur historique:', error);
      setPatientHistory([]);
    } else {
      setPatientHistory(data || []);
    }

    // Charger les suivis
    loadSuivis(patient.id);
    // Charger les prescriptions
    loadPrescriptions(patient.id);
    
    setLoading(false);
  };

  const loadSuivis = async (patientId: string) => {
    const { data } = await supabase
      .from('suivis_patients')
      .select(`
        *,
        medecin_expediteur:medecins!medecin_expediteur_id(nom, prenom, specialite),
        medecin_destinataire:medecins!medecin_destinataire_id(nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_creation', { ascending: false });

    if (data) {
      setSuivnis(data);
    }
  };

  const loadPrescriptions = async (patientId: string) => {
    const { data } = await supabase
      .from('prescriptions')
      .select(`
        *,
        medecins(nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_creation', { ascending: false });

    if (data) {
      setPrescriptions(data);
    }
  };

  const handleCreerSuiteSoins = async (patient: Patient) => {
    if (!patient) return;
    setSuiteLoadingId(patient.id);
    setSuiteActionMsg(null);
    try {
      // S'assurer d'avoir l'id du médecin connecté (si pas encore chargé)
      let medId = medecinId;
      if (!medId && user) {
        const { data: med } = await supabase
          .from('medecins')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        medId = med?.id || null;
        if (med?.id) setMedecinId(med.id);
      }

      // Une suite de soins nécessite au moins une consultation
      const { data: latestConsultations } = await supabase
        .from('consultations')
        .select('id')
        .eq('patient_id', patient.id)
        .order('date_consultation', { ascending: false })
        .limit(1);

      if (!latestConsultations || latestConsultations.length === 0) {
        setSuiteActionMsg({ type: 'error', msg: 'Impossible de créer une suite de soins : le patient n\'a encore aucune consultation.' });
        return;
      }

      // Empêcher deux suites de soins « en cours » pour un même patient
      const { data: suiteEnCours } = await supabase
        .from('suites_soins')
        .select('id')
        .eq('patient_id', patient.id)
        .eq('statut', 'en_cours')
        .limit(1)
        .maybeSingle();

      if (suiteEnCours) {
        setSuiteActionMsg({ type: 'error', msg: 'Une suite de soins est déjà en cours pour ce patient. Terminez-la avant d\'en ouvrir une nouvelle.' });
        return;
      }

      const { data: suite, error } = await supabase
        .from('suites_soins')
        .insert({
          patient_id: patient.id,
          consultation_id: latestConsultations[0].id,
          medecin_initiateur_id: medId || null,
          statut: 'en_cours',
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/medecin/suite-soins?suiteId=${suite.id}`);
    } catch (err: any) {
      console.error('Erreur création suite de soins:', err);
      setSuiteActionMsg({ type: 'error', msg: err.message || 'Impossible de créer la suite de soins.' });
    } finally {
      setSuiteLoadingId(null);
    }
  };

  const handlePartagerDossier = async () => {
    if (!selectedPatient || !selectedMedecin) return;

    const { data } = await supabase
      .from('partages_dossiers')
      .insert({
        patient_id: selectedPatient.id,
        medecin_proprietaire_id: medecinId,
        medecin_partage_id: selectedMedecin,
        date_partage: new Date().toISOString(),
        statut: 'actif'
      });

    setShowPartageModal(false);
    setSelectedMedecin('');
    alert('Dossier partagé avec succès !');
  };

  const handleCreerSuivi = async () => {
    if (!selectedPatient || !selectedMedecin || !selectedRendezVous) return;

    const { error } = await supabase
      .from('suivis_patients')
      .insert({
        patient_id: selectedPatient.id,
        medecin_expediteur_id: medecinId,
        medecin_destinataire_id: selectedMedecin,
        rendez_vous_id: selectedRendezVous.id,
        objet: suiviForm.objet,
        message: suiviForm.message,
        diagnostique: suiviForm.diagnostique,
        traitement: suiviForm.traitement,
        notes_medicales: suiviForm.notes_medicales
      });

    if (!error) {
      setShowSuiviModal(false);
      setSuiviForm({ objet: '', message: '', diagnostique: '', traitement: '', notes_medicales: '' });
      setSelectedMedecin('');
      setSelectedRendezVous(null);
      alert('Suivi envoyé avec succès !');
      loadSuivis(selectedPatient.id);
    }
  };

  const handleCreerPrescription = async () => {
    if (!selectedPatient || !selectedRendezVous) return;

    const { error } = await supabase
      .from('prescriptions')
      .insert({
        patient_id: selectedPatient.id,
        medecin_id: medecinId,
        rendez_vous_id: selectedRendezVous.id,
        medicaments: prescriptionForm.medicaments,
        posologie: prescriptionForm.posologie,
        duree_traitement: prescriptionForm.duree_traitement,
        instructions_particulieres: prescriptionForm.instructions_particulieres,
        photo_url: prescriptionForm.photo_url
      });

    if (!error) {
      setShowPrescriptionModal(false);
      setPrescriptionForm({ medicaments: '', posologie: '', duree_traitement: '', instructions_particulieres: '', photo_url: '' });
      setSelectedRendezVous(null);
      alert('Prescription créée avec succès !');
      loadPrescriptions(selectedPatient.id);
    }
  };

  const formatDate = (dateString: string, timeString?: string) => {
    const date = new Date(dateString);
    const dateFormatted = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    return timeString ? `${dateFormatted} à ${timeString}` : dateFormatted;
  };

  const getStatutBadge = (statut: string) => {
    const styles = {
      en_attente: 'bg-yellow-100 text-yellow-800',
      confirme: 'bg-blue-100 text-blue-800',
      termine: 'bg-green-100 text-green-800',
      annule: 'bg-red-100 text-red-800'
    };
    return styles[statut as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const checkForMatches = async () => {
    if (!appointmentForm.nom || !appointmentForm.prenom || !appointmentForm.date_naissance) {
      setPotentialMatches([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .ilike('nom', appointmentForm.nom)
        .ilike('prenom', appointmentForm.prenom)
        .eq('date_naissance', appointmentForm.date_naissance)
        .eq('is_provisional', false);

      if (error) throw error;

      if (data && data.length > 0) {
        setPotentialMatches(data);
        setShowMatchDialog(true);
      } else {
        setPotentialMatches([]);
      }
    } catch (err) {
      console.error('Erreur recherche correspondances:', err);
    }
  };

  useEffect(() => {
    if (showNewAppointmentModal && appointmentForm.nom && appointmentForm.prenom && appointmentForm.date_naissance) {
      const timer = setTimeout(() => {
        checkForMatches();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [appointmentForm.nom, appointmentForm.prenom, appointmentForm.date_naissance, showNewAppointmentModal]);

  const confirmMatch = (patient: any) => {
    setSelectedMatchedPatient(patient);
    setAppointmentForm({
      ...appointmentForm,
      telephone: patient.telephone || '',
      email: patient.email || '',
    });
    setShowMatchDialog(false);
    setPotentialMatches([]);
  };

  const rejectMatches = () => {
    setShowMatchDialog(false);
    setPotentialMatches([]);
    setSelectedMatchedPatient(null);
  };

  const handleCreateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!appointmentForm.nom || !appointmentForm.prenom || !appointmentForm.date_naissance) {
      setErrorMessage('Le nom, prénom et date de naissance sont obligatoires');
      return;
    }

    if (!appointmentForm.date_rdv || !appointmentForm.heure_debut || !appointmentForm.heure_fin) {
      setErrorMessage('Les informations du rendez-vous sont obligatoires');
      return;
    }

    setLoading(true);

    try {
      let patientId;

      if (selectedMatchedPatient) {
        patientId = selectedMatchedPatient.id;
      } else {
        const { data: existingPatients } = await supabase
          .from('patients')
          .select('*')
          .ilike('nom', appointmentForm.nom)
          .ilike('prenom', appointmentForm.prenom)
          .eq('date_naissance', appointmentForm.date_naissance)
          .eq('is_provisional', false);

        if (existingPatients && existingPatients.length > 0) {
          setErrorMessage('Un patient correspondant existe. Veuillez confirmer son identité avant de continuer.');
          setPotentialMatches(existingPatients);
          setShowMatchDialog(true);
          setLoading(false);
          return;
        }

        if (!appointmentForm.telephone && !appointmentForm.email) {
          setErrorMessage('Au moins un moyen de contact (téléphone ou email) est requis');
          setLoading(false);
          return;
        }

        const { data: patientData, error: patientError } = await supabase
          .from('patients')
          .insert({
            nom: appointmentForm.nom,
            prenom: appointmentForm.prenom,
            telephone: appointmentForm.telephone || null,
            email: appointmentForm.email || null,
            date_naissance: appointmentForm.date_naissance,
            is_provisional: true,
            status: 'PENDING_ACCOUNT',
            user_id: null,
          })
          .select()
          .single();

        if (patientError) throw patientError;
        patientId = patientData.id;

        if (appointmentForm.email || appointmentForm.telephone) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            
            await fetch(
              `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-patient-invitation`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session?.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  patientName: `${appointmentForm.prenom} ${appointmentForm.nom}`,
                  patientEmail: appointmentForm.email || null,
                  patientPhone: appointmentForm.telephone || null,
                  appointmentDate: appointmentForm.date_rdv,
                  appointmentTime: appointmentForm.heure_debut,
                  doctorName: `Dr. ${medecinSpecialite}`,
                }),
              }
            );
          } catch (inviteError) {
            console.error('Erreur envoi invitation:', inviteError);
          }
        }
      }

      const { error: rdvError } = await supabase
        .from('rendez_vous')
        .insert({
          patient_id: patientId,
          medecin_id: medecinId,
          date_rdv: appointmentForm.date_rdv,
          heure_debut: appointmentForm.heure_debut,
          heure_fin: appointmentForm.heure_fin,
          statut: 'en_attente',
        });

      if (rdvError) throw rdvError;

      setSuccessMessage(`Rendez-vous créé avec succès pour ${appointmentForm.prenom} ${appointmentForm.nom} le ${new Date(appointmentForm.date_rdv).toLocaleDateString('fr-FR')} à ${appointmentForm.heure_debut}.`);
      
      setAppointmentForm({
        nom: '',
        prenom: '',
        date_naissance: '',
        telephone: '',
        email: '',
        date_rdv: '',
        heure_debut: '',
        heure_fin: '',
      });
      setSelectedMatchedPatient(null);

      setTimeout(() => {
        setShowNewAppointmentModal(false);
        setSuccessMessage('');
      }, 2000);

    } catch (err: any) {
      console.error('Erreur:', err);
      setErrorMessage(err.message || 'Une erreur est survenue lors de la création du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Recherche de Patients</h1>
              <p className="mt-2 text-gray-600">
                Accédez aux dossiers des patients ayant consulté en {medecinSpecialite}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowNewAppointmentModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-clinic-green text-white rounded-lg hover:bg-clinic-green/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line"></i>
                Nouveau rendez-vous
              </button>
              <button
                onClick={() => navigate('/medecin/rendez-vous')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Rechercher un patient par nom ou prénom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchPatients()}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent text-sm"
              />
            </div>
            <button
              onClick={searchPatients}
              disabled={loading}
              className="px-6 py-3 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? 'Recherche...' : 'Rechercher'}
            </button>
          </div>
        </div>

        {suiteActionMsg && (
          <div className={`mb-6 p-4 rounded-xl text-sm flex items-center gap-2 border ${suiteActionMsg.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
            <i className={suiteActionMsg.type === 'error' ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'}></i>
            {suiteActionMsg.msg}
            <button
              onClick={() => setSuiteActionMsg(null)}
              className="ml-auto text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Résultats de recherche</h2>
              <p className="text-sm text-gray-600 mt-1">{patients.length} patient(s) trouvé(s)</p>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {patients.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-user-search-line text-5xl mb-4 text-gray-300"></i>
                  <p>Aucun patient trouvé</p>
                  <p className="text-sm mt-2">Utilisez la barre de recherche ci-dessus</p>
                </div>
              ) : (
                patients.map((patient) => (
                  <div
                    key={patient.id}
                    className={`p-4 ${
                      selectedPatient?.id === patient.id ? 'bg-clinic-blue/10 border-l-4 border-clinic-blue' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {patient.nom} {patient.prenom}
                        </h3>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                            <span>Né(e) le {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{patient.telephone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{patient.email}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleCreerSuiteSoins(patient)}
                            disabled={suiteLoadingId === patient.id}
                            className="px-3 py-1 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            {suiteLoadingId === patient.id ? (
                              <i className="ri-loader-4-line animate-spin mr-1"></i>
                            ) : (
                              <i className="ri-empathize-line mr-1"></i>
                            )}
                            Suite de soins
                          </button>
                          <button
                            onClick={() => viewPatientHistory(patient)}
                            className="px-3 py-1 bg-clinic-blue/15 text-clinic-blue-dark rounded-lg text-sm hover:bg-clinic-blue/30 transition-colors whitespace-nowrap"
                          >
                            Voir historique
                          </button>
                          <button
                            onClick={() => {
                              const basePath = (role === 'sage_femme') ? '/sage-femme' : '/medecin';
                              navigate(`${basePath}/dossier-patient/${patient.id}`);
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors whitespace-nowrap"
                          >
                            Voir dossier
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPatient(patient);
                              setShowPartageModal(true);
                            }}
                            className="px-3 py-1 bg-clinic-blue/15 text-clinic-blue-dark rounded-lg text-sm hover:bg-clinic-blue/25 transition-colors whitespace-nowrap"
                          >
                            Partager
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Historique du patient</h2>
                  {selectedPatient && (
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedPatient.nom} {selectedPatient.prenom}
                    </p>
                  )}
                </div>
                {selectedPatient && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCreerSuiteSoins(selectedPatient)}
                      disabled={suiteLoadingId === selectedPatient.id}
                      className="px-3 py-1 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {suiteLoadingId === selectedPatient.id ? (
                        <i className="ri-loader-4-line animate-spin mr-1"></i>
                      ) : (
                        <i className="ri-empathize-line mr-1"></i>
                      )}
                      Suite de soins
                    </button>
                    <button
                      onClick={() => setShowSuivisModal(true)}
                      className="px-3 py-1 bg-clinic-blue/15 text-clinic-blue-dark rounded-lg text-sm hover:bg-clinic-blue/25 transition-colors whitespace-nowrap"
                    >
                      Voir suivis
                    </button>
                    <button
                      onClick={() => setShowPrescriptionsModal(true)}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 transition-colors whitespace-nowrap"
                    >
                      Prescriptions
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {!selectedPatient ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-file-list-3-line text-5xl mb-4 text-gray-300"></i>
                  <p>Sélectionnez un patient</p>
                  <p className="text-sm mt-2">pour voir son historique de consultations</p>
                </div>
              ) : patientHistory.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-file-list-3-line text-5xl mb-4 text-gray-300"></i>
                  <p>Aucun rendez-vous trouvé</p>
                </div>
              ) : (
                patientHistory.map((rdv) => (
                  <div key={rdv.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-calendar-event-line text-clinic-blue w-4 h-4 flex items-center justify-center"></i>
                          <span className="font-medium text-gray-900">
                            {formatDate(rdv.date_rdv, rdv.heure_debut)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                          <span>
                            Dr. {rdv.medecins.nom} {rdv.medecins.prenom}
                          </span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatutBadge(rdv.statut)}`}>
                        {rdv.statut.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Motif :</span> {rdv.motif}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Spécialité : {rdv.medecins.specialite}
                      </p>
                      {rdv.diagnostique && (
                        <p className="text-sm text-gray-700 mt-2">
                          <span className="font-medium">Diagnostic :</span> {rdv.diagnostique}
                        </p>
                      )}
                    </div>
                    
                    {rdv.statut === 'termine' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedRendezVous(rdv);
                            setShowSuiviModal(true);
                          }}
                          className="px-3 py-1 bg-clinic-blue/15 text-clinic-blue-dark rounded text-xs hover:bg-clinic-blue/25 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line mr-1"></i>
                          Créer suivi
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRendezVous(rdv);
                            setShowPrescriptionModal(true);
                          }}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-file-text-line mr-1"></i>
                          Prescrire
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Partage */}
      {showPartageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Partager le dossier</h3>
              <button
                onClick={() => setShowPartageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Sélectionner un médecin</label>
              <select
                value={selectedMedecin}
                onChange={(e) => setSelectedMedecin(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8"
              >
                <option value="">Choisir un médecin...</option>
                {medecins.map((medecin) => (
                  <option key={medecin.id} value={medecin.id}>
                    Dr. {medecin.nom} {medecin.prenom} - {medecin.specialite}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPartageModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handlePartagerDossier}
                disabled={!selectedMedecin}
                className="flex-1 px-4 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark disabled:opacity-50 whitespace-nowrap"
              >
                Partager
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Créer Suivi */}
      {showSuiviModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Créer un suivi patient</h3>
              <button
                onClick={() => setShowSuiviModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Médecin destinataire</label>
                <select
                  value={selectedMedecin}
                  onChange={(e) => setSelectedMedecin(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8"
                >
                  <option value="">Choisir un médecin...</option>
                  {medecins.map((medecin) => (
                    <option key={medecin.id} value={medecin.id}>
                      Dr. {medecin.nom} {medecin.prenom} - {medecin.specialite}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Objet</label>
                <input
                  type="text"
                  value={suiviForm.objet}
                  onChange={(e) => setSuiviForm({...suiviForm, objet: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ex: Suivi post-opératoire, Avis spécialisé..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  value={suiviForm.message}
                  onChange={(e) => setSuiviForm({...suiviForm, message: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Décrire la situation du patient..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Diagnostic actuel</label>
                <textarea
                  value={suiviForm.diagnostique}
                  onChange={(e) => setSuiviForm({...suiviForm, diagnostique: e.target.value})}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Traitement en cours</label>
                <textarea
                  value={suiviForm.traitement}
                  onChange={(e) => setSuiviForm({...suiviForm, traitement: e.target.value})}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes médicales</label>
                <textarea
                  value={suiviForm.notes_medicales}
                  onChange={(e) => setSuiviForm({...suiviForm, notes_medicales: e.target.value})}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSuiviModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleCreerSuivi}
                disabled={!selectedMedecin || !suiviForm.objet || !suiviForm.message}
                className="flex-1 px-4 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark disabled:opacity-50 whitespace-nowrap"
              >
                Envoyer le suivi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Créer Prescription */}
      {showPrescriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Créer une prescription</h3>
              <button
                onClick={() => setShowPrescriptionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Médicaments</label>
                <textarea
                  value={prescriptionForm.medicaments}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, medicaments: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Lister les médicaments prescrits..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Posologie</label>
                <textarea
                  value={prescriptionForm.posologie}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, posologie: e.target.value})}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Indiquer la posologie et fréquence..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Durée du traitement</label>
                <input
                  type="text"
                  value={prescriptionForm.duree_traitement}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, duree_traitement: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ex: 7 jours, 2 semaines..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Instructions particulières</label>
                <textarea
                  value={prescriptionForm.instructions_particulieres}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, instructions_particulieres: e.target.value})}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Instructions spéciales pour le patient..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Photo de prescription (URL)</label>
                <input
                  type="url"
                  value={prescriptionForm.photo_url}
                  onChange={(e) => setPrescriptionForm({...prescriptionForm, photo_url: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="URL de la photo de l'ordonnance..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPrescriptionModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleCreerPrescription}
                disabled={!prescriptionForm.medicaments}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              >
                Créer la prescription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Voir Suivis */}
      {showSuivisModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Suivis du patient</h3>
              <button
                onClick={() => setShowSuivisModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              {suivis.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Aucun suivi trouvé</p>
              ) : (
                suivis.map((suivi) => (
                  <div key={suivi.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{suivi.objet}</h4>
                      <span className="text-xs text-gray-500">
                        {formatDate(suivi.date_creation)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      <p><strong>De:</strong> Dr. {suivi.medecin_expediteur.nom} {suivi.medecin_expediteur.prenom} ({suivi.medecin_expediteur.specialite})</p>
                      <p><strong>À:</strong> Dr. {suivi.medecin_destinataire.nom} {suivi.medecin_destinataire.prenom} ({suivi.medecin_destinataire.specialite})</p>
                    </div>
                    <div className="bg-gray-50 rounded p-3 text-sm">
                      <p><strong>Message:</strong> {suivi.message}</p>
                      {suivi.diagnostique && <p><strong>Diagnostic:</strong> {suivi.diagnostique}</p>}
                      {suivi.traitement && <p><strong>Traitement:</strong> {suivi.traitement}</p>}
                      {suivi.notes_medicales && <p><strong>Notes:</strong> {suivi.notes_medicales}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Voir Prescriptions */}
      {showPrescriptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Prescriptions du patient</h3>
              <button
                onClick={() => setShowPrescriptionsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              {prescriptions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Aucune prescription trouvée</p>
              ) : (
                prescriptions.map((prescription) => (
                  <div key={prescription.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">Prescription</h4>
                      <span className="text-xs text-gray-500">
                        {formatDate(prescription.date_creation)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      <p><strong>Médecin:</strong> Dr. {prescription.medecins.nom} {prescription.medecins.prenom} ({prescription.medecins.specialite})</p>
                    </div>
                    <div className="bg-gray-50 rounded p-3 text-sm space-y-2">
                      <p><strong>Médicaments:</strong> {prescription.medicaments}</p>
                      {prescription.posologie && <p><strong>Posologie:</strong> {prescription.posologie}</p>}
                      {prescription.duree_traitement && <p><strong>Durée:</strong> {prescription.duree_traitement}</p>}
                      {prescription.instructions_particulieres && <p><strong>Instructions:</strong> {prescription.instructions_particulieres}</p>}
                      {prescription.photo_url && (
                        <div className="mt-3">
                          <strong>Photo de l'ordonnance:</strong>
                          <img 
                            src={prescription.photo_url} 
                            alt="Ordonnance" 
                            className="mt-2 max-w-full h-auto rounded border"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Nouveau Rendez-vous */}
      {showNewAppointmentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark">Nouveau Rendez-vous</h3>
              <button
                onClick={() => {
                  setShowNewAppointmentModal(false);
                  setAppointmentForm({
                    nom: '',
                    prenom: '',
                    date_naissance: '',
                    telephone: '',
                    email: '',
                    date_rdv: '',
                    heure_debut: '',
                    heure_fin: '',
                  });
                  setSelectedMatchedPatient(null);
                  setErrorMessage('');
                  setSuccessMessage('');
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i>
                <span>{errorMessage}</span>
              </div>
            )}

            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i>
                <span>{successMessage}</span>
              </div>
            )}

            {selectedMatchedPatient && (
              <div className="mb-8 p-4 bg-green-50 border-2 border-green-200 rounded-2xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl mr-2 mt-0.5"></i>
                    <div>
                      <p className="font-semibold text-green-800">Patient confirmé</p>
                      <p className="text-green-700 mt-1">
                        {selectedMatchedPatient.prenom} {selectedMatchedPatient.nom}
                      </p>
                      <div className="flex gap-4 mt-1 text-sm text-green-600">
                        {selectedMatchedPatient.telephone && (
                          <span className="flex items-center gap-1">
                            <i className="ri-phone-line"></i>
                            {selectedMatchedPatient.telephone}
                          </span>
                        )}
                        {selectedMatchedPatient.email && (
                          <span className="flex items-center gap-1">
                            <i className="ri-mail-line"></i>
                            {selectedMatchedPatient.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedMatchedPatient(null)}
                    className="text-green-600 hover:text-green-800 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleCreateAppointment} className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center">
                  <i className="ri-user-line mr-2"></i>
                  Informations du patient
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={appointmentForm.nom}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, nom: e.target.value })}
                      disabled={!!selectedMatchedPatient}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100"
                      placeholder="Nom du patient"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      required
                      value={appointmentForm.prenom}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, prenom: e.target.value })}
                      disabled={!!selectedMatchedPatient}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100"
                      placeholder="Prénom du patient"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Date de naissance *
                    </label>
                    <input
                      type="date"
                      required
                      value={appointmentForm.date_naissance}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, date_naissance: e.target.value })}
                      disabled={!!selectedMatchedPatient}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Téléphone {!selectedMatchedPatient && '*'}
                    </label>
                    <input
                      type="tel"
                      value={appointmentForm.telephone}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, telephone: e.target.value })}
                      disabled={!!selectedMatchedPatient}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Email {!selectedMatchedPatient && '*'}
                    </label>
                    <input
                      type="email"
                      value={appointmentForm.email}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, email: e.target.value })}
                      disabled={!!selectedMatchedPatient}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100"
                      placeholder="email@exemple.com"
                    />
                  </div>
                </div>

                {!selectedMatchedPatient && (
                  <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                    <div className="flex items-start">
                      <i className="ri-information-line text-blue-600 text-xl mr-2 mt-0.5"></i>
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">Au moins un contact requis</p>
                        <p>Veuillez fournir au minimum un numéro de téléphone OU un email pour créer un patient provisoire.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  Rendez-vous
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={appointmentForm.date_rdv}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, date_rdv: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Heure de début *
                    </label>
                    <input
                      type="time"
                      required
                      value={appointmentForm.heure_debut}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, heure_debut: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Heure de fin *
                    </label>
                    <input
                      type="time"
                      required
                      value={appointmentForm.heure_fin}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, heure_fin: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewAppointmentModal(false);
                    setAppointmentForm({
                      nom: '',
                      prenom: '',
                      date_naissance: '',
                      telephone: '',
                      email: '',
                      date_rdv: '',
                      heure_debut: '',
                      heure_fin: '',
                    });
                    setSelectedMatchedPatient(null);
                    setErrorMessage('');
                    setSuccessMessage('');
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-2"></i>
                      Créer le rendez-vous
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de correspondance */}
      {showMatchDialog && potentialMatches.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center mr-4">
                <i className="ri-alert-line text-2xl text-yellow-600"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Patient potentiellement trouvé</h3>
                <p className="text-gray-600">
                  Un ou plusieurs patients avec le même nom, prénom et date de naissance existent déjà dans le système.
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
              <div className="flex items-start">
                <i className="ri-question-line text-blue-600 text-xl mr-2 mt-0.5"></i>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Veuillez demander au patient :</p>
                  <p>"Est-ce que c'est votre compte avec les informations suivantes ?"</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
              {potentialMatches.map((patient) => (
                <div
                  key={patient.id}
                  className="p-4 bg-gray-50 border-2 border-gray-200 rounded-2xl hover:border-clinic-blue transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {patient.prenom} {patient.nom}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Né(e) le {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}
                      </p>
                      <div className="flex gap-4 mt-2 text-sm">
                        {patient.telephone && (
                          <span className="flex items-center gap-1 text-gray-700">
                            <i className="ri-phone-line text-clinic-blue"></i>
                            {patient.telephone}
                          </span>
                        )}
                        {patient.email && (
                          <span className="flex items-center gap-1 text-gray-700">
                            <i className="ri-mail-line text-clinic-blue"></i>
                            {patient.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => confirmMatch(patient)}
                      className="ml-4 px-4 py-2 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-check-line mr-1"></i>
                      C'est lui
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 justify-end pt-4 border-t-2 border-gray-200">
              <button
                onClick={rejectMatches}
                className="px-6 py-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-2"></i>
                Ce n'est pas lui - Créer un patient provisoire
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
